import React, { useRef, useState, useCallback } from 'react';

/**
 * HeroVideo — premium hero video player for the homepage (under the logo).
 * Muted autoplay + loop + playsInline (browser-safe), with an elegant
 * sound toggle and a fullscreen (watch with sound) control. Navy & gold.
 *
 * Props:
 *  - src: video URL (required)
 *  - poster: optional poster image URL
 *  - vertical: true => 9:16 framing (default), false => 16:9
 *  - testid
 */
export const HeroVideo = ({ src, poster, vertical = true, testid = 'hero-video' }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      v.pause();
    }
  }, []);

  const toggleSound = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    if (!next) {
      v.volume = 1;
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
    setMuted(next);
  }, []);

  const goFullscreen = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    // Unmute for the full-screen "cinema" experience.
    v.muted = false;
    v.volume = 1;
    setMuted(false);
    const req = v.requestFullscreen || v.webkitEnterFullscreen || v.webkitRequestFullscreen || v.msRequestFullscreen;
    try { if (req) req.call(v); } catch (_e) {}
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  if (!src) return null;

  return (
    <div
      className="relative mx-auto w-full overflow-hidden rounded-2xl select-none"
      style={{
        maxWidth: vertical ? 300 : 640,
        aspectRatio: vertical ? '9 / 16' : '16 / 9',
        border: '1px solid rgba(201,168,76,0.45)',
        boxShadow: '0 24px 60px -18px rgba(0,0,0,0.8), 0 0 46px -14px rgba(201,168,76,0.25)',
        background: '#060f1f',
      }}
      data-testid={testid}
      onContextMenu={(e) => e.preventDefault()}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        className="absolute inset-0 h-full w-full object-cover pointer-events-none"
        data-testid={`${testid}-player`}
      />
      {/* transparent shield blocks right-click "Save video as" */}
      <div className="absolute inset-0 z-[5]" onContextMenu={(e) => e.preventDefault()} aria-hidden />


      {/* gold corner accents */}
      <span aria-hidden className="absolute top-0 left-0 w-9 h-9 border-t-2 border-l-2 rounded-tl-2xl" style={{ borderColor: 'rgba(201,168,76,0.5)' }} />
      <span aria-hidden className="absolute top-0 right-0 w-9 h-9 border-t-2 border-r-2 rounded-tr-2xl" style={{ borderColor: 'rgba(201,168,76,0.5)' }} />
      <span aria-hidden className="absolute bottom-0 left-0 w-9 h-9 border-b-2 border-l-2 rounded-bl-2xl" style={{ borderColor: 'rgba(201,168,76,0.5)' }} />
      <span aria-hidden className="absolute bottom-0 right-0 w-9 h-9 border-b-2 border-r-2 rounded-br-2xl" style={{ borderColor: 'rgba(201,168,76,0.5)' }} />

      {/* controls */}
      <div className="absolute bottom-3 right-3 flex items-center gap-2 z-10">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
          className="w-10 h-10 rounded-full flex items-center justify-center text-gold border transition hover:bg-black/70"
          style={{ background: 'rgba(0,0,0,0.5)', borderColor: 'rgba(201,168,76,0.4)' }}
          data-testid={`${testid}-playpause`}
        >
          {playing ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
          ) : (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          )}
        </button>
        <button
          type="button"
          onClick={toggleSound}
          aria-label="Toggle sound"
          className="w-10 h-10 rounded-full flex items-center justify-center text-gold border transition hover:bg-black/70"
          style={{ background: 'rgba(0,0,0,0.5)', borderColor: 'rgba(201,168,76,0.4)' }}
          data-testid={`${testid}-sound`}
        >
          {muted ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6" /></svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5L6 9H2v6h4l5 4V5zM15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13" /></svg>
          )}
        </button>
        <button
          type="button"
          onClick={goFullscreen}
          aria-label="Fullscreen with sound"
          className="w-10 h-10 rounded-full flex items-center justify-center text-gold border transition hover:bg-black/70"
          style={{ background: 'rgba(0,0,0,0.5)', borderColor: 'rgba(201,168,76,0.4)' }}
          data-testid={`${testid}-fullscreen`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
        </button>
      </div>

      {/* subtle "tap for sound" hint while muted */}
      {muted && (
        <button
          type="button"
          onClick={toggleSound}
          className="absolute bottom-3 left-3 z-10 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider text-gold border"
          style={{ background: 'rgba(0,0,0,0.5)', borderColor: 'rgba(201,168,76,0.35)', fontFamily: 'Georgia, serif' }}
          data-testid={`${testid}-sound-hint`}
        >
          🔊 Sound
        </button>
      )}
    </div>
  );
};

export default HeroVideo;
